import React, { useContext } from "react";
import { Card, CardContent, CardHeader } from "@altimateai/lego";
import { Typography } from "@altimateai/lego";
import { Tooltip } from "@altimateai/lego";
import { Button } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { GetOpportunitiesDataResponse } from "@/pages/explorer/types";
import StatusTag from "@/pages/explorer/components/statusTag/StatusTag";
import JiraTicketButton from "./JiraTicketButton";
import ActionsDropdown from "@/components/closingLoop/ActionsDropdown";
import { getOpportunityStatus } from "@/pages/explorer/utils";
import { InsightIcon } from "@/assets/icons";
import { IconCalendar, IconSparkles } from "@tabler/icons-react";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { WarehouseScheduleOpportunity } from "@/pages/warehouse/components/scheduler/constants";
import { useAppState } from "../app/useAppContext";
import { useInsightState } from "./context";
import { setSelectedOpportunityId } from "./context/insightSlice";
import { InsightContext } from "./context/InsightContextProvider";
import InsightDescription from "./components/Description";
import EffortInfo from "./components/EffortInfo";
import MoneySavingsInfo from "./components/MoneySavingsInfo";
import { isExplanationContext } from "./constants";
import { TimeSavingsInfo } from "@/modules/insights/components/TimeSavingsInfo";

interface InsightCardProps {
  opportunity: GetOpportunitiesDataResponse;
  setOpportunityId: (item: string) => void;
  onAskAI?: (e: React.MouseEvent, item: GetOpportunitiesDataResponse) => void;
  showTimeSavings?: boolean;
}

export const InsightCard: React.FC<InsightCardProps> = ({
  opportunity,
  setOpportunityId,
  onAskAI,
  showTimeSavings = false,
}) => {
  const { enableOpportunityTicketCreation } = useAppState();
  const { showActionsDropdown, queryId, selectedOpportunityId } = useInsightState();
  const { dispatch } = useContext(InsightContext);

  const isSelected = selectedOpportunityId === opportunity.opportunity_id;

  const isScheduleOpportunity = opportunity.opportunity_name === WarehouseScheduleOpportunity;

  const handleOpportunitySelect = (opportunityId: string | null) => {
    dispatch(setSelectedOpportunityId(opportunityId));
    setOpportunityId(opportunityId || "");
  };

  return (
    <Button
      variant="ghost"
      onClick={() => handleOpportunitySelect(opportunity.opportunity_id)}
      className="al-h-full al-text-left al-font-regular !al-whitespace-normal !al-p-0 al-w-full"
      aria-pressed={isSelected}
    >
      <Card
        className={`al-w-full al-h-full al-flex al-flex-col al-hover:shadow-md al-transition-shadow hover:al-bg-bg-brand`}
      >
        <CardHeader className="!al-p-4">
          <Stack direction="row" justify="between" align="start">
            <div className="al-flex-1">
              <Stack direction="row" align="center" gap="sm">
                <InsightIcon className="!al-w-6 !al-h-6 al-flex-shrink-0" />
                <Stack direction="column" gap="none">
                  <Typography variant="body" weight="regular" size="regular">
                    {opportunity.opportunity_name}
                  </Typography>{" "}
                  {opportunity.last_seen && (
                    <Tooltip
                      title={`This opportunity was last seen on ${dayjs(opportunity.last_seen).format("MMM D, YYYY h:mm A")}.`}
                    >
                      <Typography variant="body" className="al-text-muted-foreground" size="xs">
                        Last detected: {dayjs(opportunity.last_seen).fromNow()}
                      </Typography>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            </div>
            <Stack direction="row" align="center" gap="sm">
              {opportunity.status && (
                <StatusTag status={getOpportunityStatus(opportunity.status)} />
              )}
              <JiraTicketButton opportunityId={opportunity.opportunity_id} queryId={queryId} />
              {enableOpportunityTicketCreation && showActionsDropdown && (
                <ActionsDropdown
                  opportunity={{
                    opportunity_id: opportunity.opportunity_id,
                    title: opportunity.opportunity_name,
                    description: opportunity.description || "Query optimization opportunity",
                    impact: isExplanationContext(opportunity.explanation_context)
                      ? opportunity.explanation_context
                      : undefined,
                    savings: opportunity.money_savings?.max
                      ? parseFloat(opportunity.money_savings.max)
                      : opportunity.money_savings?.min
                        ? parseFloat(opportunity.money_savings.min)
                        : undefined,
                    category: opportunity.money_savings ? "savings" : "performance",
                    resources:
                      opportunity.resources?.map(r => ({
                        resource_name: r.resource_name,
                        resource_uri: r.resource_uri,
                      })) || [],
                  }}
                />
              )}
              {onAskAI && (
                <Tooltip title="Ask AI to help solve this opportunity">
                  <Button
                    variant="default"
                    size="icon"
                    title="Ask AI"
                    className="al-bg-blue-600 hover:al-bg-blue-700 !al-p-2 !al-h-auto !al-w-auto"
                    onClick={e => {
                      e.stopPropagation();
                      onAskAI(e, opportunity);
                    }}
                  >
                    <IconSparkles className="al-h-4 al-w-4" />
                  </Button>
                </Tooltip>
              )}
            </Stack>
          </Stack>
        </CardHeader>

        {/* eslint-disable-next-line custom-rules/forbid-classnames */}
        <CardContent className="!al-p-4 !al-pt-0 al-flex-1 al-flex al-flex-col">
          <InsightDescription opportunity={opportunity} />
          <Stack
            direction="row"
            justify="between"
            align="start"
            className="al-border-t al-pt-4 al-mt-auto"
          >
            <EffortInfo opportunity={opportunity} />
            {showTimeSavings && <TimeSavingsInfo opportunity={opportunity} />}
            <Stack align="center" gap="2xl">
              <MoneySavingsInfo opportunity={opportunity} />
              {isScheduleOpportunity && queryId && (
                <Stack direction="row" justify="end">
                  <Button variant="outline" size="sm" onClick={e => e.stopPropagation()} asChild>
                    <Link to="?tab=3">
                      <IconCalendar className="al-w-4 al-h-4 al-mr-2" />
                      View schedule
                    </Link>
                  </Button>
                </Stack>
              )}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Button>
  );
};
